// Student-facing view only: never mutate stored terms, IDs, scope or reader matching.
const str = v => typeof v === 'string' ? v.trim() : '';
const key = v => str(v).replace(/\s+/g, ' ').toLowerCase();
// Precise replacements for known overstatements only; custom author edits survive.
const TIP_UPDATES = new Map([
    ["Naming a motif scores more than saying 'she repeats it' — link it to purpose.", 'Identify the recurring pattern, then explain its purpose and effect with evidence. Naming a motif alone does not earn extra marks.'],
    ['Contrast means juxtaposition; always say what the contrast reveals.', 'Use juxtaposition when contrasting details are placed alongside each other. Explain what that arrangement reveals in this passage.'],
    ['Quote the soft-sounding words and link them to a calm or beautiful mood.', 'Quote the repeated sibilant sounds and explain their effect in context; they may suggest calm, menace or another mood.'],
    ['Antithesis signals a balanced argument — note how it makes the writer sound fair.', 'Identify the balanced opposing ideas and explain their effect in context; antithesis can sharpen a contrast without making an argument fair.'],
]);
export function terminologyExamTip(value) {
    const tip = str(value);
    return TIP_UPDATES.get(tip) || tip;
}
const CATEGORY = {
    'pack annotation': 'Text notes', 'general annotation': 'General terms',
    'key term / motif': 'Key terms & motifs', technique: 'Techniques',
    'dramatic technique': 'Dramatic techniques', context: 'Context & ideas',
    'context & ideas': 'Context & ideas', form: 'Form & structure',
    'form & structure': 'Form & structure', language: 'Language & imagery',
    'language & imagery': 'Language & imagery',
};
export function groupTerminology(terms, { globalOnly = false } = {}) {
    const groups = new Map();
    for (const term of Array.isArray(terms) ? terms : []) {
        if (!term || !str(term.name) || !str(term.definition) || (globalOnly && term.scope === 'pack')) continue;
        const id = key(term.name);
        const category = CATEGORY[key(term.category)] || str(term.category) || 'General terms';
        const variant = { ...term, name: str(term.name), definition: str(term.definition),
            example: str(term.example), tip: terminologyExamTip(term.tip), category };
        if (!groups.has(id)) groups.set(id, { id, name: variant.name, categories: [], variants: [] });
        const group = groups.get(id);
        if (!group.categories.includes(category)) group.categories.push(category);
        // Keep differing definitions/examples and pack provenance; collapse exact display duplicates.
        const signature = v => JSON.stringify([v.definition, v.example, v.tip, v.category, v.scope, v.packIds]);
        if (!group.variants.some(v => signature(v) === signature(variant))) group.variants.push(variant);
    }
    return [...groups.values()].sort((a, b) => a.name.localeCompare(b.name));
}
export function terminologyCategories(groups) {
    return [...new Set(groups.flatMap(g => g.categories))].sort((a, b) => a.localeCompare(b));
}
export function filterTerminology(groups, query, category = 'All') {
    const q = key(query);
    return groups.flatMap(group => {
        const variants = group.variants.filter(v => (category === 'All' || v.category === category)
            && (!q || [group.name, v.definition, v.example, v.tip].some(x => key(x).includes(q))));
        return variants.length ? [{ ...group, variants }] : [];
    });
}
export function terminologyPreview(terms) {
    const groups = groupTerminology(terms, { globalOnly: true });
    return ['metaphor', 'anaphora', 'juxtaposition', 'simile', 'symbolism']
        .map(id => groups.find(g => g.id === id)).filter(Boolean).slice(0, 3);
}
